import { IconExternalLink } from '@tabler/icons-react';
import type { ComponentPropsWithRef, ReactNode } from 'react';

import { useTranslation } from '../../i18n/LocaleProvider';
import { cn } from '../../lib/cn';

export type LinkVariant = 'default' | 'muted' | 'underline' | 'external';

/**
 * `visited` and `active` are set per variant rather than globally, because the
 * `--primary-active` copper is a deliberate step down from `--text-brand` — it
 * reads as "you have been here" without leaving the brand ramp.
 */
const VARIANTS: Record<LinkVariant, string> = {
  default: cn(
    'text-brand underline decoration-line-strong underline-offset-[0.2em]',
    'visited:text-primary-active',
    'hover:text-primary-hover hover:decoration-brand',
    'active:text-primary-active',
  ),
  muted: cn(
    'text-muted no-underline',
    'visited:text-muted',
    'hover:text-body hover:underline hover:decoration-line',
    'active:text-body',
  ),
  underline: cn(
    'text-body no-underline',
    'visited:text-primary-active',
    'hover:text-brand hover:underline hover:decoration-current',
    'active:text-primary-active',
  ),
  external: cn(
    'text-brand underline decoration-line-strong underline-offset-[0.2em]',
    'visited:text-primary-active',
    'hover:text-primary-hover hover:decoration-brand',
    'active:text-primary-active',
  ),
};

export interface LinkProps extends Omit<ComponentPropsWithRef<'a'>, 'children'> {
  variant?: LinkVariant;
  /**
   * Marks the link as standing alone in the flow rather than inside a sentence.
   * Only then does it take the 44px `dhd-target` overlay — an inline link sits
   * inside a text line, where that overlay would swallow the surrounding words.
   */
  standalone?: boolean;
  leadingIcon?: ReactNode;
  children?: ReactNode;
}

export function Link({
  variant = 'default',
  standalone = false,
  leadingIcon,
  children,
  className,
  ...rest
}: LinkProps) {
  const { t } = useTranslation();
  const external = variant === 'external';

  return (
    <a
      {...rest}
      {...(external
        ? { target: rest.target ?? '_blank', rel: rest.rel ?? 'noopener noreferrer' }
        : null)}
      className={cn(
        'rounded-xs transition-colors duration-[var(--dur-fast)] ease-[var(--ease-spring)]',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
        standalone && 'dhd-target relative inline-flex items-center gap-1.5',
        VARIANTS[variant],
        className,
      )}
    >
      {leadingIcon ? (
        <span
          aria-hidden="true"
          className="inline-grid size-3.5 shrink-0 place-items-center [&>svg]:size-full"
        >
          {leadingIcon}
        </span>
      ) : null}
      {children}
      {external ? (
        <>
          <span
            aria-hidden="true"
            className="inline-grid size-3.5 shrink-0 place-items-center [&>svg]:size-full"
          >
            <IconExternalLink stroke={2} />
          </span>
          {/* The icon is decorative: the fact that the link leaves the app is
              announced in words, not by a glyph a screen reader cannot read. */}
          <span className="dhd-sr-only">{t('ds.link.newTab')}</span>
        </>
      ) : null}
    </a>
  );
}
